import type { SalesChecklistItem } from "@expert/contentful";

import { useEffect } from "react";

import type { EligibleProduct } from "../../../types";

import { useSalesContext } from "../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../stores";
import { richPhraseToPlainText } from "../../product-sale-flow/components/RichPhrase";

const sessionsAlreadyPushed = new Set<string>();

/**
 * RDESQ-1988: When eligibility loads, find the `discovery` sections on the
 * recommended product's sales offer checklist and push each as its own
 * SalesGuidance message into the Expert Assist timeline. Fires at most once per
 * session.
 *
 * SCOPE: despite the name, this pushes Discovery sections ONLY — `engagement`
 * and `coreBenefits` are deliberately skipped (see the filter below and the
 * "skips engagement and coreBenefits" case in the unit test). The name and the
 * `isUsingMovedDiscoveryAndEngagementQuestions` flag both come from the wider
 * RDESQ-1988 effort, which is why they read broader than the behavior.
 *
 * MOUNT DISCIPLINE: mount this only on a host that stays mounted for the whole session — today
 * `CustomerPanel` (legacy) and `Sidecar` (redesign) — never inside a view that a layout can swap out.
 *
 * SCOPING: those hosts are NOT sales-gated, so pass `enabled` from the surface's sales flag.
 * `Sidecar` keeps the Customer tab available when sales is off (only Offers is hidden), and
 * `CustomerTab` calls `useEligibility`, so the product-offer store populates even with sales
 * disabled. Mounted unconditionally, this hook would then push Discovery guidance into a chat
 * that has sales explicitly turned off. It used to inherit that gating implicitly by living in
 * `CustomerOffers`; hoisting it to a session-long host means the gate has to be passed in.
 * Mounted in `CustomerOffers` it was deferred until the expert opened the Offers tab, because
 * Mantine's default `keepMountedMode="activity"` keeps hidden panels mounted but tears down their
 * effects. #7241 sets `keepMountedMode="display-none"` so hidden panels keep their effects, which
 * addresses that deferral; this placement is independent of it and additionally survives `OffersTab`
 * swapping `CustomerOffers` out for `ProductSaleFlowPanel` mid-sale.
 *
 * NOTE: Reads `isRecommended` directly from the store, so client-side overrides
 * applied at render time (e.g. ATT_IBC PA1 override in `categorizeProductsByType`)
 * are NOT reflected here. Acceptable for now.
 */
export function usePushDiscoveryEngagementToTimeline({ enabled = true }: { enabled?: boolean } = {}) {
    const { sessionId, taskData, features, analytics, onSendPromptToTimeline } = useSalesContext();
    const products = useProductOfferStore((state) => state.getProducts(sessionId));
    const isFlagOn = features.isUsingMovedDiscoveryAndEngagementQuestions;

    useEffect(() => {
        if (!enabled) return;
        if (!isFlagOn) return;
        if (!onSendPromptToTimeline) return;
        if (sessionsAlreadyPushed.has(sessionId)) return;
        if (products.length === 0) return;

        const locale = taskData.locale ?? "en-US";
        const recommended = findRecommendedProduct(products, locale);
        if (!recommended) return;

        const productInfo = recommended.productInfo?.[locale] ?? recommended.productInfo?.["en-US"];
        const sections = productInfo?.salesOfferChecklist?.checklistSections ?? [];

        const matching = sections.filter((s) => s.sectionType === "discovery");
        if (matching.length === 0) return;

        sessionsAlreadyPushed.add(sessionId);

        onSendPromptToTimeline({
            type: "SalesMessage",
            title: "Sales Guidance",
            text: "Ask discovery questions while solving to uncover the customer’s devices and how they use them.",
        });

        for (const section of matching) {
            onSendPromptToTimeline({
                type: "SalesGuidance",
                title: section.title,
                text: section.checklistItems.map(displayPhrase).join("\n"),
                items: section.checklistItems.map(toSalesGuidanceItem),
            });
            void analytics.dispatcher.dispatchBusinessEvent("SalesGuidanceAutoPushed", {
                productSku: recommended.productSku,
                sectionType: section.sectionType,
                itemCount: section.checklistItems.length,
            });
        }
    }, [enabled, isFlagOn, sessionId, products, taskData.locale, analytics, onSendPromptToTimeline]);
}

function findRecommendedProduct(products: EligibleProduct[], locale: string): EligibleProduct | undefined {
    return products.find((p) => (p.productInfo?.[locale] ?? p.productInfo?.["en-US"])?.isRecommended);
}

/**
 * The text to show for an item. `richPhrase` is the fuller scripted copy where it exists, so prefer
 * it — but SalesGuidance renders a bare string, so it goes through `richPhraseToPlainText` rather
 * than carrying its `==` markers into the timeline.
 */
function displayPhrase(item: SalesChecklistItem): string {
    return item.richPhrase ? richPhraseToPlainText(item.richPhrase) : item.phrase;
}

function toSalesGuidanceItem(item: SalesChecklistItem): {
    phrase: string;
    subheading?: string;
    examples?: string[];
} {
    return {
        phrase: displayPhrase(item),
        subheading: item.subheading,
        examples: item.examples,
    };
}
